import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useCommissionRate } from "../lib/commission";
import {
  ArrowLeft, Compass, SlidersHorizontal, Users, CalendarDays, PanelLeft, PanelRight,
} from "lucide-react";
import { getRegion, createJourney } from "../lib/adventureApi";
import { computeJourney } from "../lib/pricing";
import { MONTHS } from "../lib/adventureConstants";
import ModuleLibrary from "../components/adventure/ModuleLibrary";
import AdventureMap from "../components/adventure/AdventureMap";
import ItineraryPanel from "../components/adventure/ItineraryPanel";
import ModuleDetailModal from "../components/adventure/ModuleDetailModal";
import LocationDetailModal from "../components/adventure/LocationDetailModal";
import TripDetailsModal from "../components/adventure/TripDetailsModal";
import AiAssistant from "../components/adventure/AiAssistant";

let uidCounter = 0;
const nextUid = () => `it-${Date.now()}-${uidCounter++}`;

const DEFAULT_TRIP = { month: 10, travellers: 2, rooms: 1, duration: 14, fitness: "Medium", style: "Private" };

export default function Builder() {
  const { region: regionId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sourceTour = searchParams.get("tour") || null;
  const commissionRate = useCommissionRate(sourceTour);

  const [data, setData] = useState(null);
  const [items, setItems] = useState([]);
  const [serviceLevel, setServiceLevel] = useState("comfort");
  const [trip, setTrip] = useState(DEFAULT_TRIP);
  const [showTrip, setShowTrip] = useState(true);
  const [detail, setDetail] = useState(null);
  const [locDetail, setLocDetail] = useState(null);
  const [mode, setMode] = useState("build");
  const [saving, setSaving] = useState(false);
  const [mobilePanel, setMobilePanel] = useState(null); // 'lib' | 'itin' | null

  useEffect(() => {
    getRegion(regionId).then(setData).catch(() => toast.error("Could not load this region"));
  }, [regionId]);

  const modulesById = useMemo(() => Object.fromEntries((data?.modules || []).map((m) => [m.id, m])), [data]);
  const locationsById = useMemo(() => Object.fromEntries((data?.locations || []).map((l) => [l.id, l])), [data]);
  const addedIds = items.filter((i) => i.kind !== "transport").map((i) => i.module_id);
  const pricing = useMemo(() => computeJourney(items, modulesById, serviceLevel, trip.travellers),
    [items, modulesById, serviceLevel, trip.travellers]);

  const addModule = (m) => {
    if (addedIds.includes(m.id)) { toast.info(`${m.name} is already in your journey.`); return; }
    if (trip.month && !m.months.includes(trip.month)) {
      toast.error(`${m.name} isn't available in ${MONTHS[trip.month - 1]}. Try another month or experience.`);
      return;
    }
    setItems((prev) => [...prev, { uid: nextUid(), kind: "module", module_id: m.id }]);
    const loc = locationsById[m.location_id];
    toast.success(`Added ${m.name}`, { description: `Placed at ${loc?.name} on the map.` });
    if (m.acclimatisation) {
      setTimeout(() => toast("Acclimatisation recommended", {
        description: "This is a high-altitude module — allow a rest day before continuing.",
      }), 400);
    }
  };

  const onDropModule = (id) => { const m = modulesById[id]; if (m) addModule(m); };

  const resetJourney = () => {
    if (items.length === 0) return;
    const snapshot = items;
    setItems([]);
    toast("Journey reset", {
      description: "Your itinerary has been cleared. Start building again!",
      action: { label: "Undo", onClick: () => setItems(snapshot) },
    });
  };

  const removeItem = (i) => setItems((prev) => prev.filter((_, idx) => idx !== i));
  const moveItem = (i, dir) => setItems((prev) => {
    const j = i + dir; if (j < 0 || j >= prev.length) return prev;
    const copy = [...prev]; [copy[i], copy[j]] = [copy[j], copy[i]]; return copy;
  });
  const addTransfer = (index, gap, opt) => setItems((prev) => {
    const copy = [...prev];
    copy.splice(index + 1, 0, {
      uid: nextUid(), kind: "transport", transport_mode: opt.mode,
      price: opt.price, days: opt.days, from_location: gap.from, to_location: gap.to,
      label: `${gap.from} → ${gap.to} · ${opt.label}`,
    });
    return copy;
  });

  const generate = async () => {
    setSaving(true);
    try {
      const payload = {
        region: regionId, trip, service_level: serviceLevel,
        items: items.map(({ uid, ...rest }) => rest),
        source_tour_slug: sourceTour,
      };
      const { id } = await createJourney(payload);
      navigate(`/journey/${id}`);
    } catch (e) {
      toast.error("Could not create your plan. Please try again.");
      setSaving(false);
    }
  };

  if (!data) {
    return (
      <div className="h-screen flex items-center justify-center bg-base">
        <div className="flex items-center gap-3 text-ink-secondary">
          <Compass className="w-6 h-6 animate-spin text-terracotta" /> Loading the map…
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-base overflow-hidden">
      {/* Top bar */}
      <header className="flex items-center justify-between px-4 sm:px-6 h-16 border-b border-sand-dark bg-white shrink-0 z-[600]">
        <div className="flex items-center gap-3 min-w-0">
          <button data-testid="back-btn" onClick={() => navigate("/")} className="p-2 rounded-sm hover:bg-sand transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0">
            <h1 className="font-display font-extrabold text-lg tracking-tight truncate">{data.region.name}</h1>
            <p className="font-mono text-[11px] text-ink-muted uppercase tracking-widest truncate">Client trip builder</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="hidden md:flex items-center gap-3 mr-2 text-xs text-ink-secondary">
            <span className="flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5 text-moss" />{MONTHS[trip.month - 1]}</span>
            <span className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-moss" />{trip.travellers}</span>
          </div>
          <button data-testid="edit-trip-btn" onClick={() => setShowTrip(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-sm border border-sand-dark text-sm font-medium hover:border-moss transition-colors">
            <SlidersHorizontal className="w-4 h-4" /> <span className="hidden sm:inline">Trip details</span>
          </button>
        </div>
      </header>

      {/* 3-panel */}
      <div className="flex-1 flex min-h-0">
        {/* Left */}
        <aside className="hidden lg:flex w-96 border-r border-sand-dark shrink-0">
          <ModuleLibrary
            modules={data.modules} locationsById={locationsById}
            onAdd={addModule} onOpenDetail={setDetail} addedIds={addedIds} trip={trip} serviceLevel={serviceLevel}
          />
        </aside>

        {/* Center */}
        <main className="flex-1 min-w-0 relative">
          <AdventureMap
            region={data.region} locations={data.locations}
            modulesById={modulesById} locationsById={locationsById} items={items}
            onDropModule={onDropModule} mode={mode} setMode={setMode} onReset={resetJourney}
            onLocationClick={(id) => setLocDetail(id)}
            onMarkerClick={(s) => { const it = items.filter((i) => i.kind !== "transport")[s.n - 1]; if (it) setDetail(modulesById[it.module_id]); }}
          />
          {/* Mobile panel toggles */}
          <div className="lg:hidden absolute top-4 left-4 z-[500] flex flex-col gap-2">
            <button data-testid="mobile-lib-btn" onClick={() => setMobilePanel("lib")} className="p-2.5 bg-white/85 backdrop-blur-xl border border-white/50 rounded-sm shadow-sm"><PanelLeft className="w-4 h-4" /></button>
            <button data-testid="mobile-itin-btn" onClick={() => setMobilePanel("itin")} className="relative p-2.5 bg-white/85 backdrop-blur-xl border border-white/50 rounded-sm shadow-sm">
              <PanelRight className="w-4 h-4" />
              {items.length > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 bg-terracotta text-white text-[10px] rounded-full flex items-center justify-center">{items.length}</span>}
            </button>
          </div>
        </main>

        {/* Right */}
        <aside className="hidden lg:flex w-[400px] border-l border-sand-dark shrink-0">
          <ItineraryPanel
            items={items} modulesById={modulesById} locationsById={locationsById}
            serviceLevel={serviceLevel} setServiceLevel={setServiceLevel} trip={trip} pricing={pricing} commissionRate={commissionRate}
            onRemove={removeItem} onMove={moveItem} onAddTransfer={addTransfer} onOpenDetail={setDetail}
            onGenerate={generate} saving={saving}
          />
        </aside>
      </div>

      {/* Mobile overlays */}
      {mobilePanel && (
        <div className="lg:hidden fixed inset-0 z-[900] bg-black/30" onClick={() => setMobilePanel(null)}>
          <div className={`absolute top-0 bottom-0 w-[90vw] max-w-sm bg-white ${mobilePanel === "lib" ? "left-0" : "right-0"}`} onClick={(e) => e.stopPropagation()}>
            {mobilePanel === "lib" ? (
              <ModuleLibrary modules={data.modules} locationsById={locationsById}
                onAdd={addModule} onOpenDetail={setDetail} addedIds={addedIds} trip={trip} serviceLevel={serviceLevel} />
            ) : (
              <ItineraryPanel items={items} modulesById={modulesById} locationsById={locationsById}
                serviceLevel={serviceLevel} setServiceLevel={setServiceLevel} trip={trip} pricing={pricing} commissionRate={commissionRate}
                onRemove={removeItem} onMove={moveItem} onAddTransfer={addTransfer} onOpenDetail={setDetail}
                onGenerate={generate} saving={saving} />
            )}
          </div>
        </div>
      )}

      <ModuleDetailModal
        module={detail} open={!!detail} onClose={() => setDetail(null)}
        onAdd={addModule} added={detail ? addedIds.includes(detail.id) : false}
        serviceLevel={serviceLevel} locationsById={locationsById}
      />
      <LocationDetailModal
        locationId={locDetail} open={!!locDetail} onClose={() => setLocDetail(null)}
        modules={data.modules} onAddModule={addModule} addedIds={addedIds} serviceLevel={serviceLevel}
        onOpenModule={(m) => { setLocDetail(null); setTimeout(() => setDetail(m), 120); }}
      />
      <TripDetailsModal
        open={showTrip} onOpenChange={setShowTrip} trip={trip} regionName={data.region.name}
        onSave={(t) => { setTrip(t); setShowTrip(false); }}
      />
      <AiAssistant
        region={regionId} trip={trip} serviceLevel={serviceLevel}
        modulesById={modulesById} addedIds={addedIds} onAdd={addModule}
      />
    </div>
  );
}
